// src/data/disciplineAnswerLimits.js
//
// THE DISCIPLINE CLOCKS - the discipline-side twin of carrierAnswerLimits.js (§414/§415), extracted
// from the agreements' own words at cited lines and put to Bob for confirmation in
// data\YCP_DISCIPLINE_ANSWER_CLOCKS_2026-09-25.md.
//
// STATUS: EXTRACTED-UNCONFIRMED (the SME section of the .md is still open).
//
// 2026-09-26, task list section 442, on Bob's order to bring this onto master and connect it to the
// discipline screen: DisciplineCases.js's hand-typed INVESTIGATION_DEADLINES table is DELETED and the
// screen now reads screenDeadlines() below. Value-for-value this changes nothing the screen already
// showed for Amtrak, CSX and D&H/CP, and it carries the Conrail 20/20 Bob ordered in section 440. What
// it DOES change: the old table was keyed by names no case carries ('Conrail Yardmaster Agreement
// (1978)' where the lookup writes '(eff. 1978)'; CSX by the bare name where the lookup writes the
// territory), so a real Conrail or CSX-territory case silently got no deadline. Keys now resolve
// through canonicalAgreementKey, the same aliases the claim path uses. The organization-side clocks
// and the member-protection flags are NOT yet surfaced on the screen - that waits on the SME answers.
//
// WHY A MODULE, NOT A COMPONENT TABLE (§XI, and the §414 lesson): each number resolves to a verbatim
// quote at a cited line in data\agreements\*, the same quote data\YCP_VIOLATION_RULE_MAP.csv proves
// each run (VIO-1). A number hard-typed into a screen is a second source that drifts from the record;
// this is a projection of the record. null everywhere a clock is NOT in our held source - the module's
// honest "unknown", never a borrowed or placeholder number.
import { CONFIRMED, STATUS, DISCIPLINE_LIMITS } from './disciplineLimitsTable';   // section 739: the table is installation content (ruling 321)
import { canonicalAgreementKey } from './claimRequirements';
export { CONFIRMED, STATUS, DISCIPLINE_LIMITS };


// The full extracted record for an agreement, by the case's applicableAgreement (aliases resolved
// through claimRequirements.js). null when the agreement is unknown to this table. (CORRECTED, forensic audit
// 2026-10-16: this said no BN Yardmaster agreement is held. It is - the BN entry above, from the 1997 schedule - and the
// screen's old 14/14/30 "placeholder" stays withdrawn, not carried.)
export function disciplineRecordFor(applicableAgreement) {
  const key = canonicalAgreementKey(applicableAgreement);
  return (key && DISCIPLINE_LIMITS[key]) || null;
}

// The flat limits object a consumer (hearingEvents.js) reads for a case's agreement. Carries `confirmed`
// so a screen can refuse to render an unconfirmed date. A missing agreement yields all-null - the
// module's honest "no clock on file for this agreement", never a guess or a placeholder.
export function disciplineLimits(applicableAgreement) {
  const a = disciplineRecordFor(applicableAgreement);
  const n = (v) => (Number.isFinite(v) ? v : null);
  if (!a) {
    return {
      confirmed: CONFIRMED,
      noticeOfIntentDays: null, intentMeetingDays: null, hearingCommencementDays: null,
      decisionAfterHearingDays: null, appealDecisionDays: null,
      appealFilingDays: null, nonAcceptanceDays: null, boardInstitutionMonths: null,
      expeditedConferenceDays: null, expeditedDecisionDays: null, transcriptDays: null,
      appealOfficers: null, expeditedAppealOfficer: null, expeditedAppealDays: null, expeditedNoticeDays: null, appealClaimRule: null,
      appealRoutesToClaimArticle20: false, memberProtection: null, provision: 'DISC-time-limits',
    };
  }
  return {
    confirmed: CONFIRMED,
    noticeOfIntentDays: n(a.noticeOfIntentDays),
    intentMeetingDays: n(a.intentMeetingDays),
    hearingCommencementDays: n(a.hearingCommencementDays),
    decisionAfterHearingDays: n(a.decisionAfterHearingDays),
    appealDecisionDays: n(a.appealDecisionDays),
    appealFilingDays: n(a.appealFilingDays),
    nonAcceptanceDays: n(a.nonAcceptanceDays),
    boardInstitutionMonths: n(a.boardInstitutionMonths),
    expeditedConferenceDays: n(a.expeditedConferenceDays),
    expeditedDecisionDays: n(a.expeditedDecisionDays),
    transcriptDays: n(a.transcriptDays),   // T5 / D-16: only where the agreement states one (CSX 20); else null
    // Forensic audit 2026-10-16: the successive-appeal ladder and the expedited dismissal notice, where the text has them.
    appealOfficers: Array.isArray(a.appealOfficers) && a.appealOfficers.length ? a.appealOfficers.slice() : null,
    expeditedAppealOfficer: a.expeditedAppealOfficer || null,
    expeditedAppealDays: n(a.expeditedAppealDays),
    expeditedNoticeDays: n(a.expeditedNoticeDays),
    appealClaimRule: a.appealClaimRule || null,
    appealRoutesToClaimArticle20: !!a.appealRoutesToClaimArticle20,
    memberProtection: a.memberProtection || null,
    provision: a.provision,
  };
}

// WHAT THE DISCIPLINE SCREEN READS (section 442). The screen's four fields, projected from the record:
//   noticeDays   - the first carrier clock: the notice/knowledge window where the agreement has one
//                  (Amtrak 30, D&H/CP 10), else the hearing-commencement window (CSX 15, Conrail 20).
//   hearingDays  - hearingCommencementDays.
//   decisionDays - decisionAfterHearingDays.
//   intentMeetingDays - the INTENT meeting (ruling 308), from RECEIPT of the letter of intent (Amtrak 7).
//   hearingFrom  - 'first_knowledge' where the agreement runs the hearing from the supervisor's first knowledge.
// null - NOT a zero, NOT a default - when the agreement is not held or its first carrier clock is not
// in our record. The screen then says NO_DEADLINE_ON_FILE (section 364). (CORRECTED, forensic audit 2026-10-16: the
// examples here - "Conrail Mechanical Supervisors: base Rule 15 not held; BN: no agreement held" - were stale; both are
// held and both entries above carry their clocks.)
export function screenDeadlines(applicableAgreement) {
  const a = disciplineRecordFor(applicableAgreement);
  if (!a) return null;
  const n = (v) => (Number.isFinite(v) ? v : null);
  const first = n(a.noticeOfIntentDays) !== null ? n(a.noticeOfIntentDays) : n(a.hearingCommencementDays);
  if (first === null) return null;
  return {
    agreementKey: canonicalAgreementKey(applicableAgreement),
    noticeDays: first,
    hearingDays: n(a.hearingCommencementDays),
    decisionDays: n(a.decisionAfterHearingDays),
    // Ruling 308 (Bob 2026-10-12; renamed 2026-10-14): Amtrak Rule 36(a)'s meeting is the INTENT meeting - not a
    // prehearing meeting, not the hearing - and it runs from the employee's RECEIPT of the letter of intent.
    intentMeetingDays: n(a.intentMeetingDays),
    intentMeetingFrom: n(a.intentMeetingDays) !== null ? 'receipt' : null,
    noticeOfIntentDays: n(a.noticeOfIntentDays),
    hearingFrom: a.hearingFrom || null,
    provision: a.provision,
    cites: a.cites,
    confirmed: CONFIRMED,
  };
}
